import { Fragment, type FC } from 'react';

import { ThrowError } from '#apps/client-web/app/components/throw-error';
import { FixtureTypePickerDialog } from '#apps/client-web/features/audit-form-lighting-count/components/fixture-type-picker-dialog';
import { SaveChangesConfirmationDialog } from '#apps/client-web/features/audit-form-lighting-count/components/save-changes-confirmation-dialog';

import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { type ViewModelProps } from '#packages/web/types/view-model-props';
import { LoadingPage } from '#packages/web/layouts/page/templates';
import { DeleteConfirmationDialog } from '#packages/web/layouts/dialog/templates/delete-confirmation';

import { type AuditFormLightingCountEntryPageViewModel } from './audit-form-lighting-count-entry-page.view-model';
import { AuditFormLightingCountEntryPageRenderer } from './audit-form-lighting-count-entry-page.renderer';

export type AuditFormLightingCountEntryPageContainerProps =
  ViewModelProps<AuditFormLightingCountEntryPageViewModel> & LoggingContext;

export const AuditFormLightingCountEntryPageContainer: FC<
  AuditFormLightingCountEntryPageContainerProps
> = ({ viewModel }) => {
  const {
    loadAuditFormState,
    updateMutationState,
    form,
    deleteDialog,
    onDelete,
    onSubmit,
    fixtureTypeGroups,
    selectedFixtureTypeEntry,
    fixtureTypePickerDialog,
    onSelectFixtureType,
    onRequestNavigate,
    onRequestAddFixtureType,
    saveChangesConfirmDialog,
    onSaveAndNavigate,
  } = viewModel;

  return (
    <Fragment>
      {loadAuditFormState.state === 'loading' && <LoadingPage />}

      {loadAuditFormState.state === 'success' && (
        <AuditFormLightingCountEntryPageRenderer
          form={form}
          onSubmit={onSubmit}
          isPending={updateMutationState.state === 'pending'}
          onOpenFixturePicker={fixtureTypePickerDialog.open}
          selectedFixtureTypeEntry={selectedFixtureTypeEntry}
        />
      )}

      {loadAuditFormState.state === 'error' && (
        <ThrowError error={loadAuditFormState.error} />
      )}

      {updateMutationState.state === 'error' && (
        <ThrowError error={updateMutationState.error} />
      )}

      {deleteDialog.isOpen && (
        <DeleteConfirmationDialog
          open
          onCancel={deleteDialog.close}
          onConfirm={onDelete.bind(null, { confirmed: true })}
        />
      )}

      <FixtureTypePickerDialog
        open={fixtureTypePickerDialog.isOpen}
        groups={fixtureTypeGroups}
        onClose={fixtureTypePickerDialog.close}
        onSelect={onSelectFixtureType}
        onEdit={onRequestNavigate}
        onAdd={onRequestAddFixtureType}
      />

      {saveChangesConfirmDialog.isOpen && (
        <SaveChangesConfirmationDialog
          open
          onSave={onSaveAndNavigate}
          onCancel={saveChangesConfirmDialog.close}
        />
      )}
    </Fragment>
  );
};
